import useSWR from "swr";

import { CapabilityGate } from "@/components/CapabilityGate";
import { CapabilityLockedCard } from "@/components/CapabilityLockedCard";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useCapabilities } from "@/hooks/useCapabilities";
import { PlatformStatusPanel } from "./PlatformStatus";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((response) => response.json());

interface CrossSystemPanelProps {
  className?: string;
}

export function CrossSystemPanel({ className }: CrossSystemPanelProps) {
  const { capabilities } = useCapabilities();
  const canUseBooking = Boolean(capabilities?.booking_access);
  const canUsePress = Boolean(capabilities?.publicist_access);
  const canUseMusic = Boolean(capabilities?.music_access);
  const canUseCrm = Boolean(capabilities?.crm_access);

  const bookingSWR = useSWR<any[]>(canUseBooking ? "/booking/upcoming" : null, fetcher);
  const pressSWR = useSWR<any[]>(canUsePress ? "/publicist/campaigns/active" : null, fetcher);
  const releasesSWR = useSWR<any[]>(canUseMusic ? "/music/releases/current" : null, fetcher);
  const crmSWR = useSWR<any[]>(canUseCrm ? "/crm/tasks/recent" : null, fetcher);

  return (
    <div className={cn("space-y-4", className)}>
      <CapabilityGate capability="booking_access" variant="card" fallback={<CapabilityLockedCard capability="booking_access" />}>
        <LinkedSection
          title="Upcoming Shows"
          description="Bookings synced from routing + touring agents."
          items={bookingSWR.data ?? []}
          loading={bookingSWR.isLoading}
          emptyLabel="No shows scheduled."
          renderItem={(item) => ({
            id: item.id ?? item.event_id ?? item.slug ?? Math.random(),
            title: item.title ?? item.venue ?? "Unnamed show",
            subtitle: item.city ? `${item.city}${item.state ? `, ${item.state}` : ""}` : item.venue,
            meta: item.date ?? item.event_date ?? item.start_time,
          })}
        />
      </CapabilityGate>

      <CapabilityGate capability="publicist_access" variant="card" fallback={<CapabilityLockedCard capability="publicist_access" />}>
        <LinkedSection
          title="Active Press Campaigns"
          description="Live campaigns synced from Publicist."
          items={pressSWR.data ?? []}
          loading={pressSWR.isLoading}
          emptyLabel="No active campaigns."
          renderItem={(item) => ({
            id: item.id ?? item.slug ?? Math.random(),
            title: item.title ?? item.name ?? "Press Campaign",
            subtitle: item.artist ?? item.client ?? undefined,
            meta: item.status ?? "active",
          })}
        />
      </CapabilityGate>

      <CapabilityGate capability="music_access" variant="card" fallback={<CapabilityLockedCard capability="music_access" />}>
        <LinkedSection
          title="Current Releases"
          description="Release radar from the Music agent."
          items={releasesSWR.data ?? []}
          loading={releasesSWR.isLoading}
          emptyLabel="No releases in progress."
          renderItem={(item) => ({
            id: item.id ?? item.slug ?? Math.random(),
            title: item.title ?? item.name ?? "Release",
            subtitle: item.type ?? item.channel ?? undefined,
            meta: item.releaseDate ?? item.release_date ?? undefined,
          })}
        />
      </CapabilityGate>

      <CapabilityGate capability="crm_access" variant="card" fallback={<CapabilityLockedCard capability="crm_access" />}>
        <LinkedSection
          title="Recent CRM Tasks"
          description="Tasks generated by the Event Bus."
          items={crmSWR.data ?? []}
          loading={crmSWR.isLoading}
          emptyLabel="No CRM tasks yet."
          renderItem={(item) => ({
            id: item.id ?? Math.random(),
            title: item.title ?? item.summary ?? "CRM Task",
            subtitle: item.owner ?? item.assignee ?? undefined,
            meta: item.status ?? undefined,
          })}
        />
      </CapabilityGate>
    </div>
  );
}

interface LinkedSectionProps<T> {
  title: string;
  description: string;
  items: T[];
  loading?: boolean;
  emptyLabel: string;
  renderItem: (item: T) => {
    id: string | number;
    title: string;
    subtitle?: string;
    meta?: string;
  };
}

function LinkedSection<T>({
  title,
  description,
  items,
  loading,
  emptyLabel,
  renderItem,
}: LinkedSectionProps<T>) {
  return (
    <Card className="border-border/70 bg-background/40 p-4">
      <div className="mb-3">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="space-y-3 text-sm text-foreground/80">
          {items.slice(0, 4).map((item) => {
            const normalized = renderItem(item);
            return (
              <li key={normalized.id} className="rounded-lg border border-border/60 bg-background/40 p-3">
                <p className="font-medium text-foreground">{normalized.title}</p>
                {normalized.subtitle ? <p className="text-xs text-muted-foreground">{normalized.subtitle}</p> : null}
                {normalized.meta ? <p className="text-xs text-muted-foreground">{normalized.meta}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
      <PlatformStatusPanel />
